import { FC, useState } from 'react';

import {
  MailSequenceEnrollmentActivityDataFragment,
  ProgramActivityDataFragment,
  useProgramFollowUpQuery,
} from '../../../../generated/graphql';

import { useAuth } from '../../../../contexts/AuthContext';
import ProgramActivityCard from '../../../components/ProgramActivityCard';
import ProgramActivitySlideover from '../../../components/ProgramActivitySlideover';
import { isProviderUserCurrentSigner } from '../../../components/ProgramActivityCard/helpers';
import Spinner from '../../../svgs/Spinner';
import { usePatientProfileContext } from '..';
import { MAIL_SEQUENCE_FIELD_LABEL_CLASS } from '../../ScheduleMail/scheduleMailFieldLabel';
import EnrollmentActivityCard from './EnrollmentActivityCard';

interface Props {
  programFollowUpId?: string | null;
  activities: MailSequenceEnrollmentActivityDataFragment[];
}

const ScheduledSendResources: FC<Props> = ({
  programFollowUpId,
  activities,
}) => {
  const { authedProviderUser } = useAuth();
  const { patient } = usePatientProfileContext();
  const [selectedProgramActivity, setSelectedProgramActivity] =
    useState<ProgramActivityDataFragment | null>(null);
  const [isSlideoverOpen, setIsSlideoverOpen] = useState(false);

  const { data, loading } = useProgramFollowUpQuery({
    variables: { programFollowUpId: programFollowUpId! },
    skip: !programFollowUpId,
    fetchPolicy: 'cache-first',
  });

  const programFollowUp = data?.programFollowUp;
  const useSentMailTiles = Boolean(programFollowUpId && programFollowUp);

  if (programFollowUpId && loading) {
    return <Spinner className="mx-auto my-2 h-6 w-6" />;
  }

  const displayActivities = useSentMailTiles
    ? programFollowUp!.programActivities
    : activities;

  if (displayActivities.length === 0) {
    return (
      <>
        <span className={MAIL_SEQUENCE_FIELD_LABEL_CLASS}>Resources</span>
        <div className="rounded-lg border border-neutral-75 bg-neutral-25 px-4 py-3 text-small-caption text-neutral-110">
          No resources attached to this mail.
        </div>
      </>
    );
  }

  return (
    <>
      <span className={MAIL_SEQUENCE_FIELD_LABEL_CLASS}>Resources</span>
      <div className="flex flex-col gap-y-4">
        {useSentMailTiles
          ? programFollowUp!.programActivities.map((programActivity, index) => {
              const isCurrentSigner = isProviderUserCurrentSigner(
                programActivity,
                authedProviderUser?.id,
              );
              return (
                <ProgramActivityCard
                  key={programActivity.id}
                  programActivity={programActivity}
                  className={
                    index === programFollowUp!.programActivities.length - 1
                      ? 'mb-0'
                      : undefined
                  }
                  onClickProgramActivity={(activity) => {
                    setSelectedProgramActivity(activity);
                    setIsSlideoverOpen(true);
                  }}
                  authedProviderUserId={authedProviderUser?.id}
                  isCurrentSigner={isCurrentSigner}
                />
              );
            })
          : activities.map((activity, index) => (
              <EnrollmentActivityCard
                key={`${activity.title}-${activity.activityType}-${index}`}
                activity={activity}
              />
            ))}
      </div>

      <ProgramActivitySlideover
        isOpen={isSlideoverOpen}
        onClose={() => setIsSlideoverOpen(false)}
        programActivity={selectedProgramActivity}
        onVoided={async () => {
          setIsSlideoverOpen(false);
        }}
        authedProviderUserId={authedProviderUser?.id}
        programId={patient?.programInstanceId}
      />
    </>
  );
};

export default ScheduledSendResources;
